/**
 * O que faz: permite digitar imediatamente, localizar um contato existente ou iniciar um cadastro rápido.
 * Onde usa: modal de novo agendamento e edição de agendamento na Agenda.
 * Conexões: react-hook-form, lista de leads e campos de NovoClienteFields.
 * Tabelas: leads, por meio dos hooks usados no componente pai.
 * Data: 22/07/2026
 */
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Check, Loader2, Search, UserPlus } from 'lucide-react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { LeadSearchOption } from '@/hooks/useLeadsData';
import { AgendamentoFormData } from './types';

interface ClienteSelectorProps {
  form: UseFormReturn<AgendamentoFormData>;
  leads: LeadSearchOption[];
  loadingLeads: boolean;
  registrandoNovoCliente: boolean;
  setRegistrandoNovoCliente: (value: boolean) => void;
  clienteBuscaInput: string;
  setClienteBuscaInput: (value: string) => void;
}

export const ClienteSelector = ({
  form,
  leads,
  loadingLeads,
  setRegistrandoNovoCliente,
  clienteBuscaInput,
  setClienteBuscaInput,
}: ClienteSelectorProps) => {
  const [clienteComboboxOpen, setClienteComboboxOpen] = useState(false);
  const leadsSeguro = Array.isArray(leads) ? leads : [];
  const buscaNormalizada = clienteBuscaInput.trim().toLocaleLowerCase('pt-BR');
  const telefoneBuscado = clienteBuscaInput.replace(/\D/g, '');

  // Limita a prévia para manter o modal leve mesmo quando a clínica possui muitos contatos.
  const leadsFiltrados = (buscaNormalizada
    ? leadsSeguro.filter(lead =>
        lead.nome?.toLocaleLowerCase('pt-BR').includes(buscaNormalizada) ||
        (telefoneBuscado.length > 0 && lead.telefone?.replace(/\D/g, '').includes(telefoneBuscado))
      )
    : leadsSeguro
  ).slice(0, 10);

  const existeCorrespondenciaExata = leadsSeguro.some(lead =>
    lead.nome?.trim().toLocaleLowerCase('pt-BR') === buscaNormalizada ||
    (telefoneBuscado.length >= 10 && lead.telefone?.replace(/\D/g, '') === telefoneBuscado)
  );

  const selecionarCliente = (leadSelecionado: LeadSearchOption) => {
    form.setValue('cliente_id', leadSelecionado.id, { shouldValidate: true });
    form.clearErrors('cliente_id');
    setClienteBuscaInput(leadSelecionado.nome || leadSelecionado.telefone || 'Cliente sem nome');
    setRegistrandoNovoCliente(false);
    form.setValue('novo_cliente_nome', '');
    form.setValue('novo_cliente_telefone', '');
    setClienteComboboxOpen(false);
  };

  const iniciarNovoCliente = () => {
    const nomeDigitado = clienteBuscaInput.trim();
    if (nomeDigitado.length < 2) return;

    // O contato só é gravado no envio do agendamento, após nome e telefone passarem pela validação.
    form.setValue('cliente_id', '');
    form.setValue('novo_cliente_nome', nomeDigitado, { shouldValidate: true });
    setRegistrandoNovoCliente(true);
    setClienteComboboxOpen(false);
  };

  return (
    <FormField
      control={form.control}
      name="cliente_id"
      render={() => (
        <FormItem className="flex flex-col">
          <FormLabel>Cliente *</FormLabel>
          <Popover open={clienteComboboxOpen} onOpenChange={setClienteComboboxOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    role="combobox"
                    aria-expanded={clienteComboboxOpen}
                    className="pl-9 pr-9"
                    placeholder="Digite o nome ou telefone do cliente..."
                    value={clienteBuscaInput}
                    onFocus={() => setClienteComboboxOpen(true)}
                    onChange={(event) => {
                      setClienteBuscaInput(event.target.value);
                      setClienteComboboxOpen(true);

                      // Ao editar a busca, desfaz a escolha anterior para não agendar para o contato errado.
                      form.setValue('cliente_id', '', { shouldValidate: false });
                      setRegistrandoNovoCliente(false);
                      form.setValue('novo_cliente_nome', '');
                      form.setValue('novo_cliente_telefone', '');
                    }}
                  />
                  {loadingLeads && (
                    <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                </div>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[--radix-popover-trigger-width] p-0"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <Command shouldFilter={false}>
                <CommandList>
                  {leadsFiltrados.length > 0 && (
                    <CommandGroup heading="Contatos encontrados">
                      {leadsFiltrados.map(lead => (
                        <CommandItem
                          key={lead.id}
                          value={lead.id}
                          onSelect={() => selecionarCliente(lead)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              form.getValues('cliente_id') === lead.id ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span>{lead.nome || 'Cliente sem nome'} {lead.telefone ? `— ${lead.telefone}` : ''}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {buscaNormalizada.length >= 2 && !existeCorrespondenciaExata && (
                    <CommandGroup heading="Novo contato">
                      <CommandItem value={`novo:${clienteBuscaInput}`} onSelect={iniciarNovoCliente}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Criar “{clienteBuscaInput.trim()}” rapidamente
                      </CommandItem>
                    </CommandGroup>
                  )}
                  {!loadingLeads && !buscaNormalizada && leadsSeguro.length === 0 && (
                    <div className="p-3 text-sm text-muted-foreground">
                      Digite um nome para criar o primeiro contato.
                    </div>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
